import React, {useRef, useState, useEffect, useCallback} from 'react';
import {useNavigate} from "react-router-dom";
import {getTokenStatus, clearAllAuthStorage} from "utils/tokenManager";
import {API_BASE_URL} from "config/apiConfig";

// CSS imports [제거 금지]
import 'styles/Font.css';

// Image imports
import defaultProfileImg from "assets/images/icon/person-icon.svg";
import cameraIcon from "assets/images/icon/gear-icon.svg";
import searchIcon from "assets/images/icon/search-icon.svg";

// Components imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
import PostcodeModal from "components/ui/Modals/PostcodeModal/PostcodeModal";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";
import useLockScroll from "hooks/useLockScroll";

function MyProfileEdit() {
    usePreventHorizontalScroll();
    const navigate = useNavigate();

    // 동적 높이 측정을 위한 ref 및 state
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // --- State 정의 ---
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    // 주소 관련 State
    const [postCode, setPostCode] = useState('');
    const [address, setAddress] = useState('');
    const [address2, setAddress2] = useState('');

    // 마케팅 동의 State
    const [agreementMarketing, setAgreementMarketing] = useState('N');

    // 비밀번호 관련 State
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // 프로필 이미지 State
    const [profileImageSrc, setProfileImageSrc] = useState(defaultProfileImg);
    const [profileImageFile, setProfileImageFile] = useState(null);

    // 모달 관련 State
    const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
    const [modalInfo, setModalInfo] = useState(null);

    // 변경 사항 비교를 위한 초기 데이터 저장용 State
    const [initialInfo, setInitialInfo] = useState({});

    // 뷰포트 높이
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    useLockScroll(isPostcodeOpen);

    const fileInputRef = useRef(null);

    // --- [추가] 헤더 높이 측정 로직 ---
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 측정
        measureHeight();
        // 레이아웃 시프트 대비 지연 측정
        setTimeout(measureHeight, 100);

        // 리사이즈 감지
        window.addEventListener('resize', measureHeight);
        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // --- 초기 데이터 로드 ---
    useEffect(() => {
        const loadUserInfo = async () => {
            const {token} = getTokenStatus();
            if (!token) return;

            try {
                const response = await fetch(`${API_BASE_URL}/userInfo`, {
                    method: 'POST',
                    headers: {'Authorization': `Bearer ${token}`}
                });
                const data = await response.json();
                const info = data.data?.userInfo;

                if (info) {
                    setName(info.userName || '');
                    setPhoneNumber(info.phoneNumber || '');

                    const loadedPostCode = info.zipCode || info.postCode || '';
                    setPostCode(loadedPostCode);
                    setAddress(info.address || '');
                    setAddress2(info.detailAddress || '');
                    setAgreementMarketing(info.agreementMarketing || 'N');

                    setInitialInfo({
                        postCode: loadedPostCode,
                        address: info.address || '',
                        detailAddress: info.detailAddress || '',
                        agreementMarketing: info.agreementMarketing || 'N'
                    });

                    if (info.userImg) {
                        const imgUrl = info.userImg.startsWith('http')
                            ? info.userImg
                            : `${API_BASE_URL.replace(/\/api$/, '')}/${info.userImg}`;
                        setProfileImageSrc(imgUrl);
                    }
                }
            } catch (e) {
                console.error("User Info Load Error:", e);
            }
        };
        loadUserInfo();
    }, []);

    // --- 변경 사항 확인 함수 ---
    const hasChanges = useCallback(() => {
        const isImageChanged = !!profileImageFile;
        const isPasswordChanged = !!newPassword;
        const isInfoChanged =
            postCode !== initialInfo.postCode ||
            address !== initialInfo.address ||
            address2 !== initialInfo.detailAddress ||
            agreementMarketing !== initialInfo.agreementMarketing;

        return isImageChanged || isPasswordChanged || isInfoChanged;
    }, [profileImageFile, newPassword, postCode, address, address2, agreementMarketing, initialInfo]);


    // --- 뒤로가기 핸들러 ---
    const handleBackClick = () => {
        if (hasChanges()) {
            setModalInfo({
                type: 'alert',
                title: '알림',
                message: '저장하지 않은 정보는 사라집니다.\n그래도 나가시겠습니까?',
                showCancel: true,
                onConfirm: () => {
                    setModalInfo(null);
                    navigate(-1);
                }
            });
        } else {
            navigate(-1);
        }
    };


    // --- 기타 핸들러 ---
    const handleImageClick = () => fileInputRef.current?.click();
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setProfileImageSrc(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handlePostcodeComplete = (data) => {
        let addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
        if (data.userSelectedType === 'R') {
            let extraAddr = '';
            if (data.bname !== '' && /[동|로|가]$/g.test(data.bname)) extraAddr += data.bname;
            if (data.buildingName !== '' && data.apartment === 'Y') {
                extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
            }
            if (extraAddr !== '') addr += ` (${extraAddr})`;
        }
        setPostCode(data.zonecode);
        setAddress(addr);
        setIsPostcodeOpen(false);
    };

    const handleSaveClick = () => {
        if (newPassword || confirmPassword) {
            if (newPassword !== confirmPassword) {
                setModalInfo({type: 'alert', title: '알림', message: '새 비밀번호가 일치하지 않습니다.', showCancel: false});
                return;
            }
            if (newPassword.length < 4) {
                setModalInfo({type: 'alert', title: '알림', message: '비밀번호는 4자리 이상이어야 합니다.', showCancel: false});
                return;
            }
        }

        if (!hasChanges()) {
            setModalInfo({
                type: 'alert',
                title: '알림',
                message: '변경된 내용이 없습니다.',
                showCancel: false
            });
            return;
        }

        setModalInfo({
            type: 'save',
            title: '회원정보 수정',
            message: '입력하신 정보로 수정하시겠습니까?',
            showCancel: true
        });
    };

    const handleRetireClick = () => {
        setModalInfo({
            type: 'retire',
            title: '회원 탈퇴',
            message: '정말로 탈퇴하시겠습니까?\n탈퇴 시 모든 데이터가 삭제되며 복구할 수 없습니다.',
            showCancel: true
        });
    };

    const handleConfirmModal = async () => {
        const type = modalInfo?.type;

        if ((type === 'alert' || type === 'success') && !modalInfo.onConfirm) {
            setModalInfo(null);
            return;
        }

        const {token} = getTokenStatus();
        if (!token) return;

        if (type === 'save') {
            try {
                const formData = new FormData();
                if (newPassword) formData.append('password', newPassword);
                formData.append('postCode', postCode);
                formData.append('address', address);
                formData.append('detailAddress', address2);
                formData.append('agreementMarketing', agreementMarketing);

                if (profileImageFile) {
                    formData.append('userImg', profileImageFile);
                }

                const response = await fetch(`${API_BASE_URL}/userUpdate`, {
                    method: 'POST',
                    headers: {'Authorization': `Bearer ${token}`},
                    body: formData
                });

                const res = await response.json();
                const data = res.data || data;

                if (response.ok && (data.code === 200 || data.code === '200')) {
                    setModalInfo({
                        type: 'success',
                        title: '수정 완료',
                        message: '회원정보가 성공적으로 수정되었습니다.',
                        showCancel: false,
                        onConfirm: () => {
                            setModalInfo(null);
                            navigate('/my', {replace: true});
                        }
                    });
                } else {
                    throw new Error(data.message || '수정에 실패했습니다.');
                }
            } catch (e) {
                console.error("Update Error:", e);
                setModalInfo({
                    type: 'alert',
                    title: '오류',
                    message: e.message || '정보 수정 중 오류가 발생했습니다.',
                    showCancel: false
                });
            }
        }
        else if (type === 'retire') {
            try {
                const response = await fetch(`${API_BASE_URL}/retire`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    },
                    body: new URLSearchParams({}),
                });

                if (!response.ok) {
                    throw new Error(`서버 통신 오류가 발생했습니다. (Code: ${response.status})`);
                }

                const res = await response.json();
                const data = res.data || data;

                if (data.code === 200 || data.code === '200' || data.status === 'success') {
                    clearAllAuthStorage();
                    setModalInfo({
                        type: 'alert',
                        title: '탈퇴 완료',
                        message: '회원 탈퇴가 완료되었습니다.\n이용해 주셔서 감사합니다.',
                        showCancel: false,
                        onConfirm: () => {
                            setModalInfo(null);
                            navigate('/', {replace: true});
                        }
                    });
                } else {
                    throw new Error(data.massage || data.message || '탈퇴 처리에 실패했습니다.');
                }

            } catch (e) {
                console.error("Retire Error:", e);
                setModalInfo({
                    type: 'alert',
                    title: '오류',
                    message: e.message || '회원 탈퇴 중 오류가 발생했습니다.',
                    showCancel: false
                });
            }
        }
    };

    const handleCloseModal = () => {
        setModalInfo(null);
    };

    const handleConfirmClick = () => {
        if (modalInfo?.onConfirm) {
            modalInfo.onConfirm();
        } else {
            handleConfirmModal();
        }
    };

    // 동적 스타일 정의
    const dynamicMainContentStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <div style={staticStyles.backgroundWrapper}>
            {/* [수정] ref 연결 */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div style={staticStyles.statusBar}/>
                <TopNavigator
                    title="회원정보 수정"
                    onBackClick={handleBackClick}
                />
            </div>

            {/* [수정] 동적 스타일 적용 */}
            <main style={dynamicMainContentStyle}>
                <div style={staticStyles.profileImageContainer}>
                    <div style={staticStyles.imageWrapper} onClick={handleImageClick}>
                        <img src={profileImageSrc} alt="프로필" style={staticStyles.profileImage}/>
                        <div style={staticStyles.cameraIconWrapper}>
                            <img src={cameraIcon} alt="변경" style={staticStyles.cameraIcon}/>
                        </div>
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{display: 'none'}}
                        accept="image/*"
                        onChange={handleFileChange}
                    />
                </div>

                <div style={staticStyles.formContainer}>
                    <div style={staticStyles.inputGroup}>
                        <label style={staticStyles.label}>이름</label>
                        <input style={staticStyles.readOnlyInput} value={name} readOnly/>
                    </div>

                    <div style={staticStyles.inputGroup}>
                        <label style={staticStyles.label}>휴대폰 번호</label>
                        <input style={staticStyles.readOnlyInput} value={phoneNumber} readOnly/>
                    </div>

                    <div style={staticStyles.inputGroup}>
                        <label style={staticStyles.label}>주소</label>
                        <div style={staticStyles.addressInputWrapper}>
                            <input
                                style={staticStyles.input}
                                value={postCode}
                                placeholder="우편번호"
                                readOnly
                                onClick={() => setIsPostcodeOpen(true)}
                            />
                            <img
                                src={searchIcon}
                                alt="검색"
                                style={staticStyles.searchIcon}
                                onClick={() => setIsPostcodeOpen(true)}
                            />
                        </div>
                        <input
                            style={{...staticStyles.input, marginTop: '8px'}}
                            value={address}
                            placeholder="기본 주소"
                            readOnly
                            onClick={() => setIsPostcodeOpen(true)}
                        />
                        <input
                            style={{...staticStyles.input, marginTop: '8px'}}
                            value={address2}
                            onChange={(e) => setAddress2(e.target.value)}
                            placeholder="상세 주소 입력"
                        />
                    </div>

                    <div style={staticStyles.inputGroup}>
                        <label style={staticStyles.label}>비밀번호 변경</label>
                        <input
                            type="password"
                            style={staticStyles.input}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="새 비밀번호 (변경 시에만 입력)"
                        />
                        <input
                            type="password"
                            style={{...staticStyles.input, marginTop: '8px'}}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="새 비밀번호 확인"
                        />
                        {(newPassword || confirmPassword) && (
                            <p style={newPassword === confirmPassword ? staticStyles.helperTextSuccess : staticStyles.helperTextError}>
                                {newPassword === confirmPassword ? '비밀번호가 일치합니다.' : '비밀번호가 일치하지 않습니다.'}
                            </p>
                        )}
                    </div>
                </div>

                <div style={staticStyles.unsubscribeContainer}>
                    <button
                        style={staticStyles.unsubscribeButton}
                        onClick={handleRetireClick}
                    >
                        회원 탈퇴
                    </button>
                </div>

                <div style={{height: '80px'}}></div>
            </main>

            <div style={staticStyles.bottomButtonWrapper}>
                <button style={staticStyles.saveButton} onClick={handleSaveClick}>
                    수정 완료
                </button>
            </div>

            <PostcodeModal
                isOpen={isPostcodeOpen}
                onClose={() => setIsPostcodeOpen(false)}
                onComplete={handlePostcodeComplete}
                viewportHeight={viewportHeight}
            />

            <ConfirmModal
                isOpen={!!modalInfo}
                onClose={handleCloseModal}
                onConfirm={handleConfirmClick}
                title={modalInfo?.title}
                message={modalInfo?.message}
                showCancel={modalInfo?.showCancel}
            />
        </div>
    );
}

// Styles
const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D', width: '100%', height: '100dvh',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        margin: '0 auto', boxSizing: 'border-box', position: 'relative', overflow: 'hidden'
    },
    fixedHeaderWrapper: {
        position: 'fixed', top: 0, left: 0, width: '100%', zIndex: 100,
        backgroundColor: '#0D042D',
    },
    statusBar: {flexShrink: 0, backgroundColor: 'transparent'},
    mainContent: {
        // paddingTop은 동적 스타일에서 덮어쓰므로 0으로 설정, 나머지는 개별 지정
        flex: 1, overflowY: 'auto',
        paddingTop: 0,
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '100px', // 하단 버튼 공간 확보
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px',
    },
    profileImageContainer: {
        marginTop: '20px', display: 'flex', justifyContent: 'center',
    },
    imageWrapper: {
        position: 'relative', width: '80px', height: '80px', cursor: 'pointer'
    },
    profileImage: {
        width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '1px solid #3A394A'
    },
    cameraIconWrapper: {
        position: 'absolute', bottom: 0, right: 0, width: '24px', height: '24px',
        backgroundColor: '#3A394A', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center'
    },
    cameraIcon: {width: '14px', height: '14px'},
    formContainer: {width: '100%', display: 'flex', flexDirection: 'column', gap: '24px'},
    inputGroup: {display: 'flex', flexDirection: 'column', gap: '8px'},
    label: {
        fontFamily: 'Pretendard', fontWeight: 500, fontSize: '14px', color: '#9A9A9A'
    },
    input: {
        height: '48px',
        padding: '0 16px',
        borderRadius: '8px',
        border: '1px solid #3A394A',
        background: '#1B1931',
        color: '#FFFFFF',
        fontSize: '16px',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box'
    },
    readOnlyInput: {
        height: '48px',
        padding: '0 16px',
        borderRadius: '8px',
        border: '1px solid #2A2845',
        background: '#2A2845',
        color: '#747474',
        fontSize: '16px',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box'
    },
    addressInputWrapper: {position: 'relative', width: '100%'},
    searchIcon: {
        position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
        width: '20px', height: '20px', cursor: 'pointer'
    },
    helperTextSuccess: {
        fontFamily: 'Pretendard', fontSize: '12px', color: '#4CAF50', marginTop: '4px', marginLeft: '4px'
    },
    helperTextError: {
        fontFamily: 'Pretendard', fontSize: '12px', color: '#FF5252', marginTop: '4px', marginLeft: '4px'
    },
    unsubscribeContainer: {
        width: '100%', display: 'flex', justifyContent: 'center', marginTop: '10px'
    },
    unsubscribeButton: {
        background: 'transparent', border: 'none', color: '#747474',
        fontSize: '13px', textDecoration: 'underline', cursor: 'pointer', padding: '10px'
    },
    bottomButtonWrapper: {
        position: 'fixed', bottom: 0, left: 0, width: '100%',
        padding: '16px 24px calc(env(safe-area-inset-bottom) + 16px)',
        background: '#0D042D', zIndex: 100, boxSizing: 'border-box'
    },
    saveButton: {
        width: '100%', height: '56px', borderRadius: '8px', border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        color: '#FFFFFF', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer'
    }
};

export default MyProfileEdit;